import { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import {
  claimRecurringRouletteDraw,
  drawRouletteRound,
  NO_ROULETTE_CANDIDATES,
  type RouletteConfig,
} from '../services/roulette';

interface UseRouletteTimerOptions {
  eventId: string | undefined;
  djId: string | undefined;
  config: RouletteConfig | null;
}

// Runs on the manage page, not in the panel, so recurring draws keep going while the modal is closed
export function useRouletteTimer({ eventId, djId, config }: UseRouletteTimerOptions): void {
  const inFlightRef = useRef(false);
  const running = Boolean(config?.enabled && config.mode === 'recurring' && config.nextDrawAt !== null);
  const nextDrawAt = config?.nextDrawAt ?? null;

  useEffect(() => {
    if (!eventId || !djId || !running || nextDrawAt === null) return;

    const tick = async () => {
      if (inFlightRef.current || Date.now() < nextDrawAt) return;
      inFlightRef.current = true;
      try {
        const claimed = await claimRecurringRouletteDraw(eventId, Date.now());
        if (!claimed) return;
        const round = await drawRouletteRound(eventId, djId, claimed);
        toast.success(`Spotlight: "${round.songTitle}" — winner: ${round.winnerNickname} (${round.entrantCount} in the draw)`);
      } catch (error) {
        if (error instanceof Error && error.message === NO_ROULETTE_CANDIDATES) {
          toast('No queued song has voters yet — trying again next round', { icon: '🎰' });
        } else {
          console.error('[Roulette] Recurring draw failed:', error);
          toast.error("Couldn't spin the roulette");
        }
      } finally {
        inFlightRef.current = false;
      }
    };

    const interval = setInterval(() => { void tick(); }, 1000);
    return () => clearInterval(interval);
  }, [eventId, djId, running, nextDrawAt]);
}
